import type {
  PortalUserAuditChange,
  PortalUserAuditSnapshot,
} from '../../store/modules/merchant/types';
import { getPortalUserRoleLabel } from './portalUserRoleCatalog';

const FIELD_LABELS: Record<PortalUserAuditChange['field'], string> = {
  givenName: 'First name',
  familyName: 'Last name',
  roleNames: 'Roles',
  merchantIds: 'Merchant access',
};

const asStringArray = (value: unknown): string[] => (Array.isArray(value) ? value.map(String) : []);

const formatScalar = (value: unknown): string => {
  if (value == null || value === '') return '(empty)';
  return String(value);
};

export type PortalUserAuditChangeDisplay =
  | {
      field: 'givenName' | 'familyName';
      label: string;
      previous: string;
      next: string;
    }
  | {
      field: 'roleNames' | 'merchantIds';
      label: string;
      previous: string[];
      next: string[];
    };

/**
 * Map structured portal-user audit `changes` into before/after display rows.
 */
export const toPortalUserAuditChangeDisplays = (
  changes: PortalUserAuditChange[],
): PortalUserAuditChangeDisplay[] =>
  changes.map((change) => {
    if (change.field === 'givenName' || change.field === 'familyName') {
      return {
        field: change.field,
        label: FIELD_LABELS[change.field],
        previous: formatScalar(change.from),
        next: formatScalar(change.to),
      };
    }

    const mapRoles = (values: string[]) => values.map((name) => getPortalUserRoleLabel(name));

    return {
      field: change.field,
      label: FIELD_LABELS[change.field],
      previous:
        change.field === 'roleNames'
          ? mapRoles(asStringArray(change.from))
          : asStringArray(change.from),
      next:
        change.field === 'roleNames'
          ? mapRoles(asStringArray(change.to))
          : asStringArray(change.to),
    };
  });

export type PortalUserSnapshotDisplayRow = {
  label: string;
  value: string;
};

export const toPortalUserSnapshotDisplayRows = (
  snapshot: PortalUserAuditSnapshot | null,
): PortalUserSnapshotDisplayRow[] => {
  if (!snapshot) return [];

  const name = [snapshot.givenName, snapshot.familyName].filter(Boolean).join(' ').trim();
  return [
    { label: 'Name', value: name || '(empty)' },
    ...(snapshot.email ? [{ label: 'Email', value: snapshot.email }] : []),
    {
      label: 'Roles',
      value: snapshot.roleNames?.length
        ? snapshot.roleNames.map((name) => getPortalUserRoleLabel(name)).join(', ')
        : '(none)',
    },
    {
      label: 'Merchant access',
      value: snapshot.merchantIds?.length ? snapshot.merchantIds.join(', ') : '(none)',
    },
  ];
};
